import { useId, useState } from 'react';
import {
  validateName,
  type NameSelection,
  type OnboardingStep,
} from '../lib/onboarding';
interface OnboardingTextStepProps {
  step: Extract<OnboardingStep, { mode: 'text' }>;
  onSelect: (selection: NameSelection) => void;
}
/**
 * Free-text onboarding step. Currently used by the optional `name`
 * step. Mirrors OnboardingDateStep's contract: owns local state,
 * runs validation per submit attempt, emits the selection only
 * when valid (or when Skip is tapped / OK is tapped on an empty
 * field, which both resolve to `{ name: undefined }` for a clean
 * skip).
 *
 * Validation matches the server-side regex
 * (towninsider-api/src/lib/profile.ts) so the client surfaces the
 * same constraint at the same boundary — defence in depth on the
 * WRITE path. A name that fails client-side never reaches PATCH.
 *
 * Error display is gated on submit attempt rather than firing on
 * every keystroke — typing 'O' shouldn't flash an error before the
 * user gets to "O'Connor". Once they tap OK, errors stay visible
 * until they're cleared by typing a valid value (which re-evaluates
 * on the next render via the trimmed-value derivation).
 *
 * Skip semantics: explicit Skip button and empty-field OK are the
 * SAME outcome — `{ name: undefined }`. The underlying field is
 * optional server-side; skipping persists no name, persona
 * addresses the guest neutrally.
 */
export function OnboardingTextStep({ step, onSelect }: OnboardingTextStepProps) {
  const inputId = useId();
  const errorId = useId();
  const [value, setValue] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const trimmed = value.trim();
  const validationError = validateName(trimmed);
  const showError = submitAttempted && trimmed.length > 0 && validationError !== null;
  const handleSubmit = () => {
    setSubmitAttempted(true);
    if (trimmed.length === 0) {
      // Empty submit = skip — same outcome as the explicit Skip button.
      onSelect({ name: undefined });
      return;
    }
    if (validationError !== null) return; // error surfaces, no submit
    onSelect({ name: trimmed });
  };
  const handleSkip = () => {
    onSelect({ name: undefined });
  };
  return (
    <div className="flex flex-col gap-3 px-3 py-3">
      <label htmlFor={inputId} className="flex flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-wide text-moor-500">
          {step.label}
        </span>
        <input
          id={inputId}
          type="text"
          value={value}
          maxLength={step.maxLength}
          placeholder={step.placeholder}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmit();
            }
          }}
          autoComplete="given-name"
          autoCapitalize="words"
          autoFocus
          aria-invalid={showError}
          {...(showError ? { 'aria-describedby': errorId } : {})}
          className="rounded-lg border border-moor-200 bg-cream-50 px-3 py-2 text-base text-moor-900 focus:border-moor-500 focus:outline-none focus:ring-2 focus:ring-moor-500/40"
        />
      </label>
      {showError && validationError !== null && (
        <p id={errorId} role="alert" className="text-sm text-red-700">
          {validationError}
        </p>
      )}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleSkip}
          className="rounded text-sm text-moor-600 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
        >
          Skip
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="ml-auto rounded-full bg-moor-700 px-5 py-2 text-sm font-medium text-cream-50 transition-colors hover:bg-moor-800 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
        >
          OK
        </button>
      </div>
    </div>
  );
}
